import React, { forwardRef } from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

/**
 * Button component following BuilderBel Brand Guidelines
 *
 * Brand Guidelines Implementation:
 * - Primary: bg brand-600, text white; hover brand-700; active brand-800
 * - Secondary: border neutral-300, text neutral-800; hover bg neutral-50
 * - Destructive: bg danger, text white
 * - Disabled: bg brand-200, text #FFFFFFA6
 * - Sizes: LG (44–48px), MD (40px), SM (36px)
 * - Focus: 2px ring brand-400
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'secondary',
      size = 'md',
      fullWidth = false,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      className = '',
      ...props
    },
    ref
  ) => {
    const baseClasses = 'button-base';

    const variantClasses = {
      primary: 'button-primary',
      secondary: 'button-secondary',
      destructive: 'button-destructive',
    };

    const sizeClasses = {
      sm: 'button-sm',
      md: 'button-md',
      lg: 'button-lg',
    };

    const classes = [
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      fullWidth ? 'w-full' : '',
      loading ? 'cursor-not-allowed' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const iconSize = size === 'sm' ? 'icon-sm' : size === 'lg' ? 'icon-lg' : 'icon-md';

    return (
      <button ref={ref} disabled={disabled || loading} className={classes} {...props}>
        {loading && (
          <svg className={`animate-spin -ml-1 mr-2 ${iconSize}`} fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {!loading && leftIcon && <span className={`${iconSize} mr-2`}>{leftIcon}</span>}
        {children}
        {!loading && rightIcon && <span className={`${iconSize} ml-2`}>{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
